import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { IntegrationDocumentsPagePathSegment } from '@sc/graphql';
import { Fragment } from 'react';

interface Props {
  pathSegments: IntegrationDocumentsPagePathSegment[];
  onNavigate: (segmentIdx: number) => void;
}

type BreadcrumbEntry =
  | {
      type: 'collapsed';
      segments: Array<{
        segment: IntegrationDocumentsPagePathSegment;
        idx: number;
      }>;
    }
  | {
      type: 'segment';
      segment: IntegrationDocumentsPagePathSegment;
      idx: number;
    };

const buildEntries = (
  pathSegments: IntegrationDocumentsPagePathSegment[]
): BreadcrumbEntry[] => {
  const withIdx = pathSegments.map((segment, idx) => ({ segment, idx }));

  if (withIdx.length <= 3) {
    return withIdx.map((s) => ({ type: 'segment', ...s }));
  }

  const first = withIdx[0];
  const secondToLast = withIdx[withIdx.length - 2];
  const last = withIdx[withIdx.length - 1];

  if (!first || !secondToLast || !last) {
    return withIdx.map((s) => ({ type: 'segment', ...s }));
  }

  return [
    { type: 'segment', ...first },
    { type: 'collapsed', segments: withIdx.slice(1, -2) },
    { type: 'segment', ...secondToLast },
    { type: 'segment', ...last },
  ];
};

export const DocumentBrowserBreadcrumbs = ({
  pathSegments,
  onNavigate,
}: Props) => {
  const entries = buildEntries(pathSegments);

  return entries.map((entry, i) => {
    const isLast = i === entries.length - 1;

    if (entry.type === 'collapsed') {
      return (
        <Fragment key="__collapsed">
          <Menu>
            <MenuButton className="text-sm underline font-semibold hover:no-underline data-active:no-underline">
              ...
            </MenuButton>
            <MenuItems
              anchor="bottom start"
              className="border flex flex-col bg-background-secondary border-grey-t-800 rounded-xs text-white p-1 space-y-1 z-60 outline-none"
            >
              {entry.segments.map(({ segment, idx }) => (
                <MenuItem key={segment.id}>
                  <button
                    className="px-3 py-2 text-left text-sm hover:bg-grey-t-800 rounded-xs"
                    onClick={() => onNavigate(idx)}
                  >
                    {segment.name}
                  </button>
                </MenuItem>
              ))}
            </MenuItems>
          </Menu>
          <span className="text-grey-600">/</span>
        </Fragment>
      );
    }

    return (
      <Fragment key={entry.segment.id}>
        {isLast ? (
          <span className="text-sm font-semibold text-secondary-400">
            {entry.segment.name}
          </span>
        ) : (
          <>
            <button
              className="text-sm underline font-semibold hover:no-underline"
              onClick={() => onNavigate(entry.idx)}
            >
              {entry.segment.name}
            </button>
            <span className="text-grey-600">/</span>
          </>
        )}
      </Fragment>
    );
  });
};
